import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api, unwrap, unwrapPage } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components, operations } from '@/api/schema';

export type ElementoBiblioteca = components['schemas']['LibraryItem'];
export type ElementoDetalle = components['schemas']['LibraryItemDetail'];
export type ElementoBody = components['schemas']['LibraryItemUpdateBody'];
export type LicenciaBiblioteca = components['schemas']['LibraryMembership'];
export type FiltrosBiblioteca = NonNullable<
  operations['getAdminLibraryItems']['parameters']['query']
>;

export function useResumenBiblioteca() {
  return useQuery({
    queryKey: ['contenido', 'resumen'],
    queryFn: () => unwrap(api.GET('/api/v1/admin/library-items/summary')),
  });
}

export function useBiblioteca(query: FiltrosBiblioteca) {
  return useQuery({
    queryKey: ['contenido', 'biblioteca', query],
    queryFn: () =>
      unwrapPage<ElementoBiblioteca[]>(
        api.GET('/api/v1/admin/library-items', { params: { query } }),
      ),
    placeholderData: keepPreviousData,
  });
}

export function useElemento(id: number | null) {
  return useQuery({
    queryKey: ['contenido', 'elemento', id],
    queryFn: () =>
      unwrap<ElementoDetalle>(
        api.GET('/api/v1/admin/library-items/{id}', { params: { path: { id: String(id) } } }),
      ),
    enabled: id !== null,
  });
}

/** Agrega o quita elementos de licencias. Refresca índice, conteos y el elemento abierto. */
export function useAsignar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: components['schemas']['LibraryAssignBody']) =>
      unwrap(api.POST('/api/v1/admin/library-items/assignments', { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['contenido'] }),
    onError: (e) => toast.error(mensajeDe(e)),
  });
}

/** Licencias activas primero; las de instructores aparte, como en el legacy. */
export function agruparLicencias(lista: LicenciaBiblioteca[]) {
  const instructores = (m: LicenciaBiblioteca) => /instructor/i.test(m.name);
  return [
    { titulo: 'Activas', items: lista.filter((m) => m.isActive && !instructores(m)) },
    { titulo: 'Para instructores', items: lista.filter((m) => m.isActive && instructores(m)) },
    { titulo: 'Inactivas', items: lista.filter((m) => !m.isActive) },
  ].filter((g) => g.items.length > 0);
}
